import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { AthleteStatusChangeView, AthleteView, SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';

export interface AthleteStatusControlsProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  athlete: AthleteView;
  /** Reloads the list after a change so the new status and version are shown. */
  onChanged: () => Promise<void>;
}

type LeavingStatus = 'archived' | 'withdrawn';

const confirmKeys: Readonly<Record<LeavingStatus, TranslationKey>> = {
  archived: 'athletes.status.hint.archive',
  withdrawn: 'athletes.status.hint.withdraw',
};

/** Archive or withdraw (with a reason) and reactivate; what was ended is reported afterwards. */
export function AthleteStatusControls({
  locale,
  base,
  csrfToken,
  athlete,
  onChanged,
}: AthleteStatusControlsProps): React.JSX.Element {
  const [leaving, setLeaving] = useState<LeavingStatus | null>(null);
  const [reason, setReason] = useState('');
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);
  const [outcome, setOutcome] = useState<AthleteStatusChangeView | null>(null);

  async function change(status: 'active' | LeavingStatus): Promise<void> {
    setErrorKey(null);
    try {
      const result = await apiRequest<AthleteStatusChangeView>(
        `${base}/athletes/${athlete.id}/status`,
        {
          method: 'POST',
          body: status === 'active' ? { status } : { status, reason },
          csrfToken,
          ifMatchVersion: athlete.version,
        },
      );
      setOutcome(result);
      setLeaving(null);
      setReason('');
      await onChanged();
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  return (
    <Box sx={{ mt: 1 }}>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {outcome !== null && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'athletes.status.done')}{' '}
          {translate(locale, 'athletes.status.ended.memberships')}: {outcome.clubMembershipsEnded}
          {' · '}
          {translate(locale, 'athletes.status.ended.teams')}: {outcome.teamPlacementsEnded}
          {' · '}
          {translate(locale, 'athletes.status.ended.groups')}: {outcome.groupPlacementsEnded}
          {' · '}
          {translate(locale, 'athletes.status.ended.links')}: {outcome.guardianLinksEnded}
        </Alert>
      )}
      {athlete.status !== 'active' && (
        <Button
          size="small"
          onClick={() => {
            void change('active');
          }}
        >
          {translate(locale, 'athletes.status.reactivate')}
        </Button>
      )}
      {athlete.status === 'active' && leaving === null && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={() => {
              setLeaving('archived');
            }}
          >
            {translate(locale, 'athletes.status.archive')}
          </Button>
          <Button
            size="small"
            color="warning"
            onClick={() => {
              setLeaving('withdrawn');
            }}
          >
            {translate(locale, 'athletes.status.withdraw')}
          </Button>
        </Stack>
      )}
      {athlete.status === 'active' && leaving !== null && (
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            void change(leaving);
          }}
        >
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {translate(locale, confirmKeys[leaving])}
          </Typography>
          <TextField
            required
            size="small"
            fullWidth
            multiline
            label={translate(locale, 'athletes.status.reason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              color="warning"
              disabled={reason.trim() === ''}
            >
              {translate(
                locale,
                leaving === 'archived' ? 'athletes.status.archive' : 'athletes.status.withdraw',
              )}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setLeaving(null);
                setReason('');
              }}
            >
              {translate(locale, 'athletes.edit.cancel')}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
